import { IPaymentOfferModel, UserStore } from "@nf/common";
import { ROUTES } from "@nf/constants";
import React, { useCallback } from "react";
import { useDispatch } from "react-redux";

import {
  useCurrentSubscription,
  useOffers,
  useRouter,
  useSwitchOffer,
} from "../../../../hooks";

import styles from "../ChooseSubscription/ChooseSubscription.module.scss";

import dynamic from "next/dynamic";

const SimpleOfferCard = dynamic(() => import("components/SimpleOfferCard"));

const PaymentHeader = dynamic(
  () => import("screens/PaymentScreen/components/PaymentHeader")
);

const ChangeSubscriptionDialog = dynamic(
  () =>
    import(
      "components/CancellationDialog/steps/Confirmation/ChangeSubscriptionDialog"
    )
);

const DotsLoader = dynamic(() => import("components/DotsLoader"));

export type ChangeSubscriptionProps = {
  offers?: IPaymentOfferModel[];
};

export const ChangeSubscription: React.FC<ChangeSubscriptionProps> = () => {
  const [selectedOffer, setSelectedOffer] =
    React.useState<IPaymentOfferModel>();
  const currentSubscription = useCurrentSubscription();
  const dispatch = useDispatch();
  const router = useRouter();

  const { offers, areOffersLoading } = useOffers();

  const {
    mutate: switchOffer,
    isLoading,
    error,
  } = useSwitchOffer({
    onSuccess: () => {
      dispatch(UserStore.Actions.getUserSubscriptions());

      router.replace(ROUTES.SETTINGS_SUBSCRIPTION, undefined, {
        withLoadingSplash: true,
      });
    },
  });

  const selectOffer = useCallback(
    (offer: IPaymentOfferModel) => {
      if (currentSubscription?.OfferId === offer.Id) {
        return router.replace(ROUTES.SETTINGS_SUBSCRIPTION);
      }

      setSelectedOffer(offer);
    },
    [currentSubscription?.OfferId]
  );

  const onCancel = () => setSelectedOffer(undefined);

  const onConfirm = () => {
    if (currentSubscription?.Id && selectedOffer?.Id) {
      switchOffer({
        subscription_id: currentSubscription.Id,
        to_offer_id: selectedOffer.Id,
      });
    }
  };

  if (!offers || areOffersLoading) {
    return <DotsLoader />;
  }

  return (
    <div className={styles.container}>
      <PaymentHeader hasOffers={offers && offers.length > 0} />

      <div className={styles.container}>
        <div className={styles.offers}>
          {offers.map((offer) => (
            <SimpleOfferCard
              key={offer.Id}
              offer={offer}
              active={offer.Id === currentSubscription?.OfferId}
              displayMode="change"
              handleSelect={() => selectOffer(offer)}
            />
          ))}
        </div>
      </div>

      {selectedOffer && (
        <ChangeSubscriptionDialog
          offer={selectedOffer}
          isLoading={isLoading}
          error={error}
          onCancel={onCancel}
          onConfirm={onConfirm}
        />
      )}
    </div>
  );
};

export default ChangeSubscription;
